import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const RUNTIME_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Virtual tour banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'An apartment interior image — living room, kitchen, bedroom, or furnished model unit — that is unique to this page. Never use logos, brand marks, floor plan drawings, line art, or unit layout graphics.',
    context: 'Full-bleed banner hero on the Virtual Tours page. Use an interior unit photo only; avoid exteriors, amenities, maps, and floor plan drawings. Must be a different photo from every other page hero, especially Availability and Pricing. Do not reuse other page hero images. Never use logos or floor plan layout images.',
  },
};

export function VirtualTourHero() {
  const bgImage = useSlotGroup('virtualtourhero.bg_image', RUNTIME_DEF);

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath="virtualtourhero.bg_image" definition={RUNTIME_DEF} className="absolute inset-0">
        {bgImage.image ? (
          <VibesitesImage url={bgImage.image} alt={bgImage.alt || 'Virtual tour banner'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/30" />
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent path="virtualtourhero.title" as="h1" className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider" />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourHero',
  displayName: 'Virtual Tour Hero',
  tokens: {
    'virtualtourhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Virtual Tour</p>',
    },
  },
  slotGroups: {
    'virtualtourhero.bg_image': RUNTIME_DEF,
  },
};

(VirtualTourHero as any).contentSchema = contentSchema;

export default VirtualTourHero;
